import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Link,
  SelectChangeEvent,
  Tab,
  Tabs,
  LinearProgress,
  Tooltip,
  Chip,
  Stack,
} from '@mui/material';
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro';
import { CloudUpload, Description, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import useAuth from '../context/AuthProvider';
import { apiClient } from '../helper/api';
import { formatUtcToLocal } from '../helper/utils';
import Swal from 'sweetalert2';
import { getRowsPerPageOptions } from '../components/AddEdit/CustomFunctions';
import { RiExpandUpDownLine } from 'react-icons/ri';

const ALLOWED_FILE_TYPES = ['.pdf', '.doc', '.docx', '.xls', '.xlsx'] as const;
const FILE_ACCEPT_ATTRIBUTE = '.pdf,.doc,.docx,.xls,.xlsx';

const REFERENCE_DOCUMENTS = {
  NON_PREMIUM: {
    path: '/assets/brand/NonPremiumEndorsements.docx',
    downloadName: 'NonPremiumEndorsements.docx',
    displayName: 'NonPremiumEndorsements'
  }
} as const;

interface UploadedFile {
  id: string;
  file: File;
  name: string;
  size: number;
  blobAddress?: string;
}

interface State {
  stateId: number;
  stateName: string;
  postalAbbv: string;
}

interface Partner {
    partnersId: number;
    customerName: string;
  }

interface PriorSubmission {
  nonPremiumEndorsementId?: number;
  document?: string;
  client?: string;
  state?: string;
  blobAddress?: string;
  fileSizeKB?: number;
  dateReceived?: string;
  }
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'] as const;
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

const createFileObject = (file: File): UploadedFile => ({
  id: Math.random().toString(36).substr(2, 9),
  file,
  name: file.name,
  size: file.size,
  blobAddress: URL.createObjectURL(file)
});

const downloadDocument = (path: string, fileName: string): void => {
  const link = document.createElement('a');
  link.href = path;
  link.download = fileName;
  link.target = '_blank';
  link.click();
};

const NonPremiumEndorsements: React.FC<{
  sortModel: any;
  setSortModel: React.Dispatch<React.SetStateAction<any>>;
}> = ({
  sortModel,
  setSortModel
}) => {
  const { user } = useAuth();
  const token = user?.token;
  
  const [selectedState, setSelectedState] = useState<string>('');
  const [selectedPartner, setSelectedPartner] = useState<string>('');
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showReferenceDialog, setShowReferenceDialog] = useState(false);
  const [showReferenceSheetDialog, setShowReferenceSheetDialog] = useState(false);
  const [states, setStates] = useState<State[]>([]);
  const [statesLoading, setStatesLoading] = useState(true);
  const [partners, setPartners] = useState<Partner[]>([]);
  
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isSubmittingRef = useRef<boolean>(false);
  const [selectedTab, setSelectedTab] = useState<'new' | 'prior'>('new');
  const [priorSubmissions, setPriorSubmissions] = useState<PriorSubmission[]>([]);
  const [priorSubmissionsLoading, setPriorSubmissionsLoading] = useState(false);
  const [priorSubmissionsPageSize, setPriorSubmissionsPageSize] = useState(10);
  const [priorSubmissionsCurrentPage, setPriorSubmissionsCurrentPage] = useState(1);
  const [priorSubmissionsTotalCount, setPriorSubmissionsTotalCount] = useState(0);

  const sectionTitleSx = {
      color: "text.secondary",
      fontWeight: 600,
      letterSpacing: "0.08em",
      display: "block",
      pl: 1,
      // mb: 1,
  };

  useEffect(() => {
    if (token) {
      fetchStates();
      getPartnersList();
    }
  }, [token]);

  // Fetch states from API
    const fetchStates = async () => {
      try {
        setStatesLoading(true);
        const response = await apiClient('/States/getStates', {
          method: 'GET',
          headers: new Headers({
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          }),
        });

        if (response && response.data) {
          const allowedStates = ['California', 'Nevada', 'Pennsylvania', 'Texas', 'Washington'];
          const filteredStates = response.data.filter((state: State) => 
            allowedStates.includes(state.stateName)
          );
          setStates(filteredStates);
        }
      } catch (error) {
        console.error('Error fetching states:', error);
        setStates([]);
      } finally {
        setStatesLoading(false);
      }
    };


  const getPartnersList = () => {
    apiClient("/customer/getPartnersList", {
      method: "GET",
      headers: new Headers({
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      }),
    })
      .then((responseJson) => {
        if (!responseJson) return;
        
        if (responseJson.data && responseJson.data.length > 0) {
          setPartners(responseJson.data);
        } else {
          setPartners([]);
        }
      })
      .catch((error) => {
        setPartners([]);
      });
  };

  useEffect(() => {
    if(partners.length === 1){
      setSelectedPartner(partners[0].customerName);
    }
  }, [partners]);

  const fetchPriorSubmissions = useCallback(async () => {
    if (!token) return;
    
    setPriorSubmissionsLoading(true);
    try {
      const payload = {
        pageSize: priorSubmissionsPageSize,
        pageNumber: priorSubmissionsCurrentPage
      };

      const response = await apiClient('/NonPremiumEndorsement/getPriorSubmissiondocuments', {
        method: 'POST',
        headers: new Headers({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        }),
        body: JSON.stringify(payload),
      });

      if (response?.data) {
        setPriorSubmissions(response.data);
        setPriorSubmissionsTotalCount(response.totalCount || 0);
      } else {
        setPriorSubmissions([]);
        setPriorSubmissionsTotalCount(0);
      }
    } catch (error) {
      console.error('Error fetching prior submissions:', error);
      setPriorSubmissions([]);
      setPriorSubmissionsTotalCount(0);
    } finally {
      setPriorSubmissionsLoading(false);
    }
  }, [token, priorSubmissionsPageSize, priorSubmissionsCurrentPage]);
  useEffect(() => {
    if (selectedTab === 'prior') {
      fetchPriorSubmissions();
    }
  }, [selectedTab, fetchPriorSubmissions]);

  const handleFileSelect = (files: FileList | null) => {
    if (!files) return;
    
    const invalidFiles: string[] = [];
    
    // Check file types
    Array.from(files).forEach(file => {
      const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
      if (!ALLOWED_FILE_TYPES.includes(fileExtension as any)) {
        invalidFiles.push(file.name);
      }
    });
    
    // Show error if invalid files found
    if (invalidFiles.length > 0) {
      Swal.fire({
        title: 'Invalid File Type',
        text: `Only PDF, DOC, DOCX, XLS, and XLSX files are allowed. Invalid files: ${invalidFiles.join(', ')}`,
        icon: 'error',
        confirmButtonColor: '#02A4E3'
      });
      return;
    }
    
    const newFiles = Array.from(files).map(createFileObject);
    setUploadedFiles(prev => [...prev, ...newFiles]);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };


  const removeFile = (fileId: string) => {
    setUploadedFiles(prev => {
      const fileToRemove = prev.find(file => file.id === fileId);
      if (fileToRemove?.blobAddress) {
        URL.revokeObjectURL(fileToRemove.blobAddress);
      }
      return prev.filter(file => file.id !== fileId);
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };



  const uploadedFilesColumns: GridColDef[] = useMemo(() => [
    {
      field: 'fileName',
      headerName: 'File Name',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Link 
          href={params.row.blobAddress} 
          target="_blank" 
          sx={{ 
            textDecoration: 'none', 
            overflow: 'initial', 
            whiteSpace: 'break-spaces',
            lineHeight: 'normal',
            color: '#162E3C',
            cursor: 'pointer',
            '&:hover': {
              textDecoration: 'underline'
            }
          }}
        >
          {params.value || '-'}
        </Link>
      ),
    },
    {
      field: 'sizeText',
      headerName: 'File Size',
      flex: 0.6,
      minWidth: 120,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      flex: 0.5,
      minWidth: 120,
      renderCell: (params) => (
        <Tooltip title="Remove this file from the list" arrow>
          <IconButton
            color="error"
            onClick={() => removeFile(params.row.id)}
            aria-label={`Remove ${params.row.fileName || 'file'}`}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 6H5M5 6H21M5 6V20C5 20.5304 5.21071 21.0391 5.58579 21.4142C5.96086 21.7893 6.46957 22 7 22H17C17.5304 22 18.0391 21.7893 18.4142 21.4142C18.7893 21.0391 19 20.5304 19 20V6H5ZM8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </IconButton>
        </Tooltip>
        ),
      },
    ],
    [removeFile]
  );

  const dataGridRows = useMemo(() => 
    uploadedFiles.map((fileObj) => ({
      id: fileObj.id,
      fileName: fileObj.name,
      blobAddress: fileObj.blobAddress,
      sizeText: formatFileSize(fileObj.size),
      file: fileObj.file
    })), [uploadedFiles]
  );
  // Prior Submissions rows - optimized
  const priorSubmissionsRows = useMemo(() => {
    return priorSubmissions.map((row, index) => ({
      ...row,
      id: row.nonPremiumEndorsementId,
      documentName: row.document || '-',
      client: row.client || '-',
      state: row.state || '-',
      fileSizeInKB: row.fileSizeKB || '-',
    }));
  }, [priorSubmissions]);
  const priorSubmissionsColumns: GridColDef[] = useMemo(() => [
    {
      field: 'client',
      headerName: 'Client',
      flex: 2,
      minWidth: 150,
      hideSortIcons: true,
      renderHeader: (params) => {
        const sortItem = sortModel.find(
            (item: any) => item.field === 'dueDate'
          );
    
        const isSortedAsc = sortItem?.sort === 'asc';
        const isSortedDesc = sortItem?.sort === 'desc'
        return (
            <div >
                <span className="table-card-title word-wrap-1 p-0">{params.colDef.headerName}</span>
                {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
            </div>
        )
    },
    },
    {
      field: 'state',
      headerName: 'State',
      flex: 1.5,
      minWidth: 120,
      hideSortIcons: true,
      renderHeader: (params) => {
        const sortItem = sortModel.find(
            (item: any) => item.field === 'state'
          );
    
        const isSortedAsc = sortItem?.sort === 'asc';
        const isSortedDesc = sortItem?.sort === 'desc'
        return (
            <div >
                <span className="table-card-title word-wrap-1 p-0">{params.colDef.headerName}</span>
                {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
            </div>
        )
    },
    },
    {
      field: 'documentName',
      headerName: 'Document',
      flex: 3,
      minWidth: 200,
      hideSortIcons: true,
      renderHeader: (params) => {
        const sortItem = sortModel.find(
            (item: any) => item.field === 'documentName'
          );
    
        const isSortedAsc = sortItem?.sort === 'asc';
        const isSortedDesc = sortItem?.sort === 'desc'
        return (
            <div >
                <span className="table-card-title word-wrap-1 p-0">{params.colDef.headerName}</span>
                {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
            </div>
        )
    },
      renderCell: (params) => {
        const { documentName, blobAddress } = params.row;
        
        if (blobAddress && documentName && documentName !== '-') {
          return (
            <Link 
              href={blobAddress} 
              target="_blank" 
              rel="noopener noreferrer"
              sx={{ 
                textDecoration: 'none', 
                overflow: 'initial', 
                whiteSpace: 'break-spaces',
                lineHeight: 'normal',
                color: '#162E3C',
                cursor: 'pointer',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              {documentName}
            </Link>
          );
        }
        return <Typography>{documentName}</Typography>;
      },
    },
    {
      field: 'dateReceived',
      headerName: 'Date Submitted',
      flex: 1.5,
      minWidth: 120,
      hideSortIcons: true,
      renderHeader: (params) => {
        const sortItem = sortModel.find(
            (item: any) => item.field === 'dateReceived'
          );
    
        const isSortedAsc = sortItem?.sort === 'asc';
        const isSortedDesc = sortItem?.sort === 'desc'
        return (
            <div >
                <span className="table-card-title word-wrap-1 p-0">{params.colDef.headerName}</span>
                {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
            </div>
        )
    },
      renderCell: (params) => {
        const dateReceived = params.row.dateReceived;
        if (!dateReceived) {
          return <Typography>-</Typography>;
        }
        try {
          const formattedDate = formatUtcToLocal(dateReceived, 'MM/DD/YYYY');
          return formattedDate !== 'N/A' ? <Typography>{formattedDate}</Typography> : <Typography>-</Typography>;
        } catch (error) {
          return <Typography>-</Typography>;
        }
      },
    },
    {
      field: 'fileSizeInKB',
      headerName: 'File Size (KB)',
      flex: 1.5,
      minWidth: 120,
      hideSortIcons: true,
      renderHeader: (params) => {
        const sortItem = sortModel.find(
            (item: any) => item.field === 'fileSizeInKB'
          );
    
        const isSortedAsc = sortItem?.sort === 'asc';
        const isSortedDesc = sortItem?.sort === 'desc'
        return (
            <div >
                <span className="table-card-title word-wrap-1 p-0">{params.colDef.headerName}</span>
                {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
            </div>
        )
    },
      renderCell: (params) => {
        const fileSizeKB = params.row.fileSizeInKB;
        if (!fileSizeKB || fileSizeKB === '-' || fileSizeKB === '0') {
          return <Typography>-</Typography>;
        }
        return <Typography>{typeof fileSizeKB === 'number' ? `${fileSizeKB.toFixed(2)} KB` : `${fileSizeKB} KB`}</Typography>;
      },
    },
  ], []);

  const selectedStateData = states.find(state => state.stateName === selectedState);
  const selectedPartnerData = partners.find(partner => partner.customerName === selectedPartner);

 

  const handleSendToMISC = async () => {
    
    if (isSubmittingRef.current) {
      return;
    }
    
    if (!selectedState) {
      Swal.fire({
        title: 'Please Select Filing State',
        text: 'You must select a filing state before sending documents to MISC.',
        icon: 'warning',
        confirmButtonColor: '#02A4E3'
      });
      return;
    }

    // Determine the partner to use - either selected or auto-select if only one
    let partnerToUse = selectedPartner;
    if (!partnerToUse && partners.length === 1) {
      partnerToUse = partners[0].customerName;
      setSelectedPartner(partners[0].customerName); // Update state for UI consistency
    }
    
    if (!partnerToUse) {
      Swal.fire({
        title: 'Please Select Client',
        text: 'You must select a client before sending documents to MISC.',
        icon: 'warning',
        confirmButtonColor: '#02A4E3'
      });
      return;
    }

    if (uploadedFiles.length === 0) {
      Swal.fire({
        title: 'No Documents Uploaded',
        text: 'You must upload at least one document before sending to MISC.',
        icon: 'warning',
        confirmButtonColor: '#02A4E3'
      });
      return;
    }

    isSubmittingRef.current = true;
    setLoading(true);
    
    try {
      const formData = new FormData();
      
      const selectedStateData = states.find(state => state.stateName === selectedState);
      const stateId = selectedStateData?.stateId || 0;
      
      const selectedPartnerData = partners.find(partner => partner.customerName === partnerToUse);
      const partnersId = selectedPartnerData?.partnersId || 0;
      
      formData.append('stateId', stateId.toString());
      formData.append('partnersId', partnersId.toString());
      
      uploadedFiles.forEach((fileObj, index) => {
        formData.append(`file_${index}`, fileObj.file);
        formData.append(`fileName_${index}`, fileObj.name);
        formData.append(`fileSize_${index}`, (fileObj.size / 1024).toFixed(2));
      });

      const response = await apiClient('/NonPremiumEndorsement/Upload', {
        method: 'POST',
        headers: new Headers({
          Authorization: `Bearer ${token}`,
        }),
        body: formData
      });

      if (response?.status === true) {
        Swal.fire({
          title: 'Success!',
          text: response.message || 'Files uploaded successfully',
          icon: 'success',
          confirmButtonColor: '#02A4E3',
          customClass: {
            confirmButton: 'btn-radius',
            popup: 'card-radius'
          }
        }).then(() => {
          // Reset form
          setSelectedState('');
          setSelectedPartner('');
          setUploadedFiles([]);
        });
      }
    } catch (error) {
      console.error('Error submitting endorsements:', error);
      Swal.fire({
        title: 'Submission Error',
        text: 'There was an error submitting your documents. Please try again.',
        icon: 'error',
        confirmButtonColor: '#02A4E3'
      });
    } finally {
      isSubmittingRef.current = false;
      setLoading(false);
    }
  };


  const BORDER = '#e2e8f0';
  const PRIMARY = '#2099d2';

  return (
    <Box sx={{ width: '100%', padding: { xs: 2, md: 3 }, paddingTop: 1 }}>
      <div style={{ position: 'fixed', top: 0, left: 0, zIndex: 9990, width: '100%' }}>
        {loading && <LinearProgress color="info" sx={{ backgroundColor: '#132D3D' }} />}
      </div>

      <Box
        sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexDirection: { xs: 'column', md: 'row' },
            gap: 1.5,
            px: 2,
            py: 1.5,
            borderBottom: "1px solid #e2e8f0",
            backgroundColor: "#fafbfc",
            minHeight: 56,
            marginLeft: { xs: '-24px', md: '0px' },
            marginRight: { xs: '-24px', md: '0px' },
        }}
      >
        <div className='py-2' style={{ display: 'flex', flexDirection: 'column' }}>
          <h4 className="mb-0" style={{ fontWeight: 600, color: "#0f172a" }}>Non-Premium Endorsements</h4>
          <Typography sx={{ fontSize: "0.75rem", fontWeight: 400, color: "#64748b" }}>
            Welcome, <strong>{user?.userFirstName} {user?.userlastName}</strong>! Select the filing state and upload your non-premium endorsements, or view prior submissions below.
          </Typography>
        </div>
      </Box>

      {/* Top bar: Tabs + Reference card */}
      <Box
        sx={{
          my: 3,
          p: 2.5,
          borderRadius: '12px',
          border: `1px solid ${BORDER}`,
          backgroundColor: '#ffffff',
          boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 2,
            mb: 3,
          }}
        >
          <Tabs
            value={selectedTab}
            onChange={(_, newValue: 'new' | 'prior') => setSelectedTab(newValue)}
            sx={{
              minHeight: 48,
              '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.9375rem' },
              '& .Mui-selected': { color: PRIMARY },
              '& .MuiTabs-indicator': {
                backgroundColor: PRIMARY,
                height: 3,
                borderRadius: '3px 3px 0 0',
              },
            }}
          >
            <Tab value="new" label="+ New Submission" />
            <Tab value="prior" label="Prior Submissions" />
          </Tabs>
          <Tooltip title="View state-specific filing requirements and guidelines" arrow placement="top">
            <Button
              variant="outlined"
              size="medium"
              onClick={() => setShowReferenceSheetDialog(true)}
              aria-label="View reference sheet: state requirements and guidelines"
              startIcon={<Description />}
              sx={{
                marginLeft: 'auto',
                textTransform: 'none',
                fontWeight: 600,
                borderColor: '#e2e8f0',
                color: '#475569',
                '&:hover': {
                  borderColor: '#2099d2',
                  backgroundColor: '#2099d214',
                  color: '#2099d2',
                },
              }}
            >
              Reference Sheet
            </Button>
          </Tooltip>
        </Box>
        
        <Typography variant="body1" sx={{ mb: 2, color: '#64748b', fontSize: '0.9375rem' }}>
          {selectedTab === 'new'
            ? 'Please select the filing state and upload your non-premium endorsements in the space below.'
            : 'View all previously submitted documents below.'}
        </Typography>

        {/* Important Note - Only show for New Submission tab */}
        {selectedTab === 'new' && (
          <Box
            sx={{
              backgroundColor: '#fffbeb',
              border: '1px solid #fcd34d',
              borderRadius: '12px',
              padding: 2,
              mb: 2,
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 500, color: '#92400e', textAlign: 'center' }}>
              <strong>🚨 NOTE:</strong> Please DO NOT upload $0 New and Renewal Business policies on this screen. Contact{' '}
              <Link href="mailto:MISCFilings@morrisisc.com" sx={{ color: PRIMARY, fontWeight: 600 }}>
                MISCFilings@morrisisc.com
              </Link>{' '}
              for further assistance as there may be additional filing requirements & procedures based on the home state.
            </Typography>
          </Box>
        )}

        {/* Main Content Area - Full Width */}
      <Box>
        {selectedTab === 'new' ? (
          <>
          {/* Step 1: Submission details */}
          <Box
            sx={{
              p: 2,
              borderRadius: '12px',
              border: `1px solid ${BORDER}`,
              backgroundColor: '#f8fafc',
              mb: 3,
            }}
          >
            <Typography variant="subtitle2" sx={{ color: '#64748b', fontWeight: 600, mb: 2, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.75rem' }}>
              Step 1 — Submission details
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
              Choose the filing state and client (if you have more than one).
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flexWrap="wrap" useFlexGap>
              <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 240 } }} disabled={statesLoading}>
                <InputLabel id="filing-state-label">Filing State *</InputLabel>
                <Select
                  labelId="filing-state-label"
                  value={selectedState}
                  label="Filing State *"
                  onChange={(e: SelectChangeEvent<string>) => setSelectedState(e.target.value)}
                  sx={{ '& .MuiOutlinedInput-notchedOutline': { borderColor: '#c4c4c4' } }}
                >
                  <MenuItem value=""><em>Select State</em></MenuItem>
                  {states.map((state) => (
                    <MenuItem key={state.stateId} value={state.stateName}>
                      {state.stateName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {partners.length > 1 && (
                <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 240 } }}>
                  <InputLabel id="partner-label">Select Client *</InputLabel>
                  <Select
                    labelId="partner-label"
                    value={selectedPartner}
                    label="Select Client *"
                    onChange={(e: SelectChangeEvent<string>) => setSelectedPartner(e.target.value)}
                    sx={{ '& .MuiOutlinedInput-notchedOutline': { borderColor: '#c4c4c4' } }}
                  >
                    <MenuItem value=""><em>Select Client</em></MenuItem>
                    {partners.map((partner) => (
                      <MenuItem key={partner.partnersId} value={partner.customerName}>
                        {partner.customerName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Stack>
          </Box>

          {/* Step 2: File Upload Area */}
          <Box 
          sx={{
            p: 2,
            borderRadius: '12px',
            border: `1px solid ${BORDER}`,
            backgroundColor: '#f8fafc',
            mb: 3,
          }}
          >
            <Typography variant="subtitle2" sx={{ color: '#64748b', fontWeight: 600, mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.75rem' }}>
              Step 2 — Upload documents
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748b', mb: 1.5 }}>
              Add one or more files. You can click to browse or drag and drop.
            </Typography>
            <Paper
              component="div"
              role="button"
              tabIndex={0}
              aria-label="Upload files: click or drag and drop. Accepted: PDF, DOC, DOCX, XLS, XLSX"
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  fileInputRef.current?.click();
                }
              }}
              sx={{
                border: isDragOver ? `2px dashed ${PRIMARY}` : `1px dashed ${BORDER}`,
                borderRadius: '12px',
                padding: 4,
                textAlign: 'center',
                backgroundColor: isDragOver ? '#f0f9ff' : '#FFFFFF',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                minHeight: 200,
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                boxShadow: isDragOver ? '0 2px 12px rgba(32, 153, 210, 0.12)' : '0 1px 3px rgba(15, 23, 42, 0.06)',
                '&:focus': { outline: `2px solid ${PRIMARY}`, outlineOffset: 2 },
              }}
              onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
              onDragLeave={(e) => { e.preventDefault(); setIsDragOver(false); }}
              onDrop={(e) => {
                e.preventDefault();
                setIsDragOver(false);
                handleFileSelect(e.dataTransfer.files);
              }}
              onClick={() => fileInputRef.current?.click()}
            >
              <CloudUpload sx={{ fontSize: 48, color: PRIMARY, mb: 2 }} />
              <Typography variant="h6" sx={{ color: '#334155', fontWeight: 600, mb: 1 }}>
                Select file or drag and drop
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
                Multiple files supported
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent="center" sx={{ gap: 1 }}>
                {['PDF', 'DOC', 'DOCX', 'XLS', 'XLSX'].map((ext) => (
                  <Chip key={ext} label={ext} size="small" sx={{ backgroundColor: '#f1f5f9', color: '#475569', fontWeight: 500 }} />
                ))}
              </Stack>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={FILE_ACCEPT_ATTRIBUTE}
                style={{ display: 'none' }}
                onChange={(e) => handleFileSelect(e.target.files)}
              />
            </Paper>
          </Box>

        {/* Uploaded Files DataGrid */}
        {uploadedFiles.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Box
              sx={{
                backgroundColor: '#ffffff',
                borderRadius: '12px',
                border: `1px solid ${BORDER}`,
                boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
                overflow: 'hidden',
                p: 2,
                pb: 0,
              }}
            >
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: '#334155' }}>
                Attached Documents ({uploadedFiles.length})
              </Typography>
              <DataGridPro
                autoHeight
                rows={dataGridRows}
                columns={uploadedFilesColumns}
                disableSelectionOnClick
                disableColumnMenu
                pageSize={pageSize}
                pagination
                rowCount={uploadedFiles.length}
                paginationMode="client"
                onPageChange={setCurrentPage}
                onPageSizeChange={(newPageSize) => {
                  setPageSize(newPageSize);
                  setCurrentPage(0);
                }}
                rowsPerPageOptions={getRowsPerPageOptions(uploadedFiles.length, pageSize)}
                sx={{
                  border: 'none',
                  fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                  fontSize: '14px',
                  color: '#475569',
                  '& .MuiDataGrid-columnHeaderTitle': {
                    whiteSpace: 'normal',
                    lineHeight: 1.35,
                    fontWeight: 600,
                    fontSize: '12px',
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                    color: '#64748b',
                  },
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: '#f8fafc',
                    borderBottom: `2px solid ${BORDER}`,
                  },
                  '& .MuiDataGrid-row': {
                    '&:nth-of-type(even)': { backgroundColor: '#fafbfc' },
                    '&:hover': { backgroundColor: '#f1f5f9 !important' },
                  },
                  '& .MuiDataGrid-cell': { borderBottom: '1px solid #f1f5f9', color: '#334155' },
                  '& .MuiDataGrid-footerContainer': {
                    borderTop: `1px solid ${BORDER}`,
                    backgroundColor: '#f8fafc',
                  },
                }}
              />
            </Box>
          </Box>
        )}

          {/* Step 3: Review and send */}
          <Box
            sx={{
              p: 2,
              borderRadius: '12px',
              border: `1px solid ${BORDER}`,
              backgroundColor: '#f8fafc',
              mb: 3,
            }}
          >
            <Typography variant="subtitle2" sx={{ color: '#64748b', fontWeight: 600, mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.75rem' }}>
              Step 3 — Review and send
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              {uploadedFiles.length > 0 && (
                <Typography variant="body2" sx={{ color: '#64748b' }}>
                  <strong>{uploadedFiles.length}</strong> file{uploadedFiles.length !== 1 ? 's' : ''} ready to send
                </Typography>
              )}
              {uploadedFiles.length === 0 && selectedState && (
                <Typography variant="body2" sx={{ color: '#64748b' }}>
                  Add at least one document to continue
                </Typography>
              )}
              {!selectedState && (
                <Typography variant="body2" sx={{ color: '#64748b' }}>
                  Select a filing state and add documents to continue
                </Typography>
              )}
            </Box>
            <Tooltip
              title={
                !selectedState
                  ? 'Please select a filing state first'
                  : uploadedFiles.length === 0
                    ? 'Please add at least one document'
                    : loading
                      ? 'Sending…'
                      : 'Send your documents to MISC'
              }
              arrow
            >
              <span>
                <Button
                  variant="contained"
                  size="medium"
                  startIcon={<SendOutlinedIcon />}
                  onClick={handleSendToMISC}
                  disabled={loading || !selectedState || uploadedFiles.length === 0}
                  sx={{ textTransform: 'none', fontWeight: 600, backgroundColor: '#2099d2', '&:hover': { backgroundColor: '#176990' }, px: 2.5 }}
                >
                  {loading ? 'Sending…' : 'Send to MISC'}
                </Button>
              </span>
            </Tooltip>
            </Box>
          </Box>
          </>
        ) : (
          /* Prior Submissions View */
          <Box
            sx={{
              backgroundColor: '#ffffff',
              borderRadius: '12px',
              border: `1px solid ${BORDER}`,
              boxShadow: '0 4px 24px rgba(15, 23, 42, 0.08), 0 1px 3px rgba(15, 23, 42, 0.06)',
              overflow: 'hidden',
              mb: 3,
            }}
          >
            <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${BORDER}`, backgroundColor: '#f8fafc' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#334155' }}>
                Prior Submissions ({priorSubmissionsTotalCount || 0})
              </Typography>
            </Box>
            <Box sx={{ width: '100%', height: 'calc(100vh - 380px)', minHeight: 400 }}>
              <DataGridPro
                rows={priorSubmissionsRows}
                columns={priorSubmissionsColumns}
                getRowId={(row) => row.id}
                disableSelectionOnClick
                disableColumnMenu
                pageSize={priorSubmissionsPageSize}
                pagination
                rowCount={priorSubmissionsTotalCount}
                paginationMode="server"
                loading={priorSubmissionsLoading}
                onPageChange={(newPage) => setPriorSubmissionsCurrentPage(newPage + 1)}
                onPageSizeChange={(newPageSize) => {
                  setPriorSubmissionsPageSize(newPageSize);
                  setPriorSubmissionsCurrentPage(1);
                }}
                rowsPerPageOptions={[10, 25, 50]}
                sx={{
                  fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                  border: 'none',
                  fontSize: '14px',
                  color: '#475569',
                  backgroundColor: '#ffffff',
                  '& .MuiDataGridPro-columnHeaderTitle': {
                      whiteSpace: 'normal',
                      lineHeight: 1.35,
                      fontWeight: 600,
                      fontSize: '12px',
                      letterSpacing: '0.05em',
                      textTransform: 'uppercase',
                      color: '#64748b',
                  },
                  '&.MuiDataGrid-root .MuiDataGrid-columnHeader--alignRight .MuiDataGrid-columnHeaderTitleContainer': {
                      pl: 1,
                  },
                  '& .MuiDataGrid-columnHeaders': {
                      backgroundColor: '#f8fafc',
                      borderBottom: '2px solid #e2e8f0',
                      minHeight: '52px !important',
                      maxHeight: '52px !important',
                  },
                  '& .MuiDataGrid-columnHeader': {
                      '&:focus, &:focus-within': { outline: 'none' },
                  },
                  '& .MuiDataGrid-row': {
                      transition: 'background-color 0.15s ease',
                      '&:nth-of-type(even)': {
                          backgroundColor: '#fafbfc',
                      },
                      '&:hover': {
                          backgroundColor: '#f1f5f9 !important',
                      },
                  },
                  '& .MuiDataGrid-cell': {
                      borderBottom: '1px solid #f1f5f9',
                      fontSize: '14px',
                      color: '#334155',
                      overflow: 'visible',
                      whiteSpace: 'normal',
                      lineHeight: 'normal',
                      wordWrap: 'break-word',
                      textOverflow: 'clip',
                      '&:focus': { outline: 'none' },
                  },
                  '& .MuiDataGrid-virtualScroller': {
                      border: 'none',
                  },
                  '& .MuiDataGrid-main': {
                      border: 'none',
                  },
                  '& .MuiDataGrid-footerContainer': {
                      borderTop: '1px solid #e2e8f0',
                      backgroundColor: '#f8fafc',
                      minHeight: 56,
                  },
                  '& .MuiTablePagination-root': {
                      color: '#64748b',
                      fontSize: '13px',
                  },
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                      marginBottom: 0,
                  },
                  '& .MuiTablePagination-select': {
                      fontSize: '13px',
                      borderRadius: '6px',
                      border: '1px solid #e2e8f0',
                  },
                  '& .MuiIconButton-root': {
                      color: '#64748b',
                      '&:hover': {
                          backgroundColor: '#f1f5f9',
                          color: '#475569',
                      },
                  },
                }}
                components={{
                  NoRowsOverlay: () => (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                      <Typography variant="body1" sx={{ color: '#64748b' }}>
                        No prior submissions found.
                      </Typography>
                    </Box>
                  ),
                }}
              />
            </Box>
          </Box>
        )}
      </Box>
      </Box>
      <Dialog
        open={showReferenceSheetDialog}
        onClose={() => setShowReferenceSheetDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            width: '90%',
            maxWidth: 900,
            minHeight: '40vh',
            maxHeight: '90vh',
            borderRadius: 2,
            boxShadow: '0 24px 48px rgba(0,0,0,0.12)',
            overflow: 'hidden',
          },
        }}
        sx={{ '& .MuiDialog-paper': { overflow: 'hidden' } }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            py: 2,
            px: 3,
            background: 'linear-gradient(135deg, #0d4a63 0%, #15607a 100%)',
            color: '#fff',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', flex: 1, minWidth: 0 }}>
            <DescriptionOutlinedIcon sx={{ fontSize: 28, opacity: 0.95, flexShrink: 0 }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography component="span" variant="h6" sx={{ fontWeight: 600, letterSpacing: '0.02em', display: 'block' }}>
                Non-Premium Endorsement Requirements
              </Typography>
              <Typography component="span" variant="body2" sx={{ opacity: 0.95, display: 'block', lineHeight: 1 }}>
                Reference Sheet
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            <Box
              component="img"
              src="/assets/brand/MISC_Full_Type_Lockup_Primary.png"
              alt="MISC"
              sx={{ height: 40, width: 'auto', objectFit: 'contain', opacity: 0.95 }}
            />
            <IconButton
              onClick={() => setShowReferenceSheetDialog(false)}
              sx={{
                color: 'rgba(255,255,255,0.9)',
                '&:hover': { color: '#fff', backgroundColor: 'rgba(255,255,255,0.12)' },
              }}
              aria-label="Close dialog"
            >
              <CloseOutlinedIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: 'grey.50', px: 3, py: 2, overflow: 'auto' }}>
          <Box sx={{ maxHeight: '65vh', overflow: 'auto' }}>
            {/* CALIFORNIA Section */}
            <Typography variant="overline" sx={{...sectionTitleSx, paddingTop: '15px' }}>CALIFORNIA</Typography>
            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 3, backgroundColor: '#fff', borderColor: 'divider' }}>
              {/* <Typography variant="h6" sx={{ color: '#02A4E3', fontWeight: 'bold', mb: 2 }}>
                CALIFORNIA
              </Typography> */}
              
              {/* Nil Endorsements Content */}
              <Box sx={{ mb: 2, textAlign: 'left' }}>
                <Typography variant="h6" sx={{color: '#02A4E3', fontWeight: 'bold', mb: 2 }}>
                  Nil Endorsements
                </Typography>
                
                <Typography variant="body1" sx={{ mb: 2 ,textAlign:'left',fontSize:'18px'}}>
                   Although the SLA accepts all nil endorsements for review, the following nil endorsements must be submitted to us to update our database:
                </Typography>
                
                <Box component="ul" sx={{ pl: 2, mb: 2 }}>
                  <Typography component="li" variant="body1" sx={{ mb: 0.5 }}>
                    Policy number change
                  </Typography>
                  <Typography component="li" variant="body1" sx={{ mb: 0.5 }}>
                    Insured name change
                  </Typography>
                  <Typography component="li" variant="body1" sx={{ mb: 0.5 }}>
                    Insured address change
                  </Typography>
                  <Typography component="li" variant="body1" sx={{ mb: 0.5 }}>
                    Policy effective date change
                  </Typography>
                  <Typography component="li" variant="body1" sx={{ mb: 0.5 }}>
                    Policy expiration date change*
                  </Typography>
                  <Typography component="li" variant="body1" sx={{ mb: 0.5 }}>
                    Change of insurer
                  </Typography>
                  <Typography component="li" variant="body1" sx={{ mb: 0.5 }}>
                    Change of coverage
                  </Typography>
                </Box>
                
                <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#666',textAlign:'left' }}>
                  *Endorsements that extend the original policy period are not considered part of the nil endorsement process and should follow the same filing requirements as a renewal.
                </Typography>
              </Box>
              <Link href="https://www.slacal.com/docs/default-source/general-content-documents/tutorials/nil-endorsements.pdf" target="_blank" rel="noopener noreferrer" sx={{ color: '#02A4E3'}}>
              Filing Requirements with the Surplus Line Association (slacal.com)
              </Link>
            </Paper>

            {/* NEVADA Section */}
            <Typography variant="overline" sx={{...sectionTitleSx}}>NEVADA</Typography>
            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 3, backgroundColor: '#fff', borderColor: 'divider' }}>
              {/* <Typography variant="h6" sx={{ color: '#02A4E3', fontWeight: 'bold', mb: 2 }}>
                NEVADA
              </Typography> */}
              <Typography variant="body1" sx={{ fontWeight: 'bold',textAlign:'left' }}>
                What transaction types am I required to file?
              </Typography>
              <Typography variant="body1" sx={{ mb: 2,  textAlign:'justify' }}>
                <strong>Answer:</strong> NAC 685A.240 A filing must be made for a new or a renewal of a contract of insurance. If the coverage and category code does not appear on the approved export list, the filing must include declination details. You are also required to make an amended filing for each cancellation and <strong> each endorsement which changes the named insured, the description or location of the subject of insurance, the coverage, conditions, terms, premiums, or participating insurers.</strong> Endorsements extending the policy for any length of time are filed as extensions and treated the same as renewals.
              </Typography>
              <Link href="https://www.nsla.org/Resources/FAQs" target="_blank" rel="noopener noreferrer" sx={{ color: '#02A4E3' }}>
              https://www.nsla.org/Resources/FAQs
              </Link>
            </Paper>

            {/* PENNSYLVANIA Section */}
            <Typography variant="overline" sx={{...sectionTitleSx}}>PENNSYLVANIA</Typography>
            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 3, backgroundColor: '#fff', borderColor: 'divider' }}>
              {/* <Typography variant="h6" sx={{ color: '#02A4E3', fontWeight: 'bold', mb: 2 }}>
                PENNSYLVANIA
              </Typography> */}
              <Typography  sx={{ mb: 2 ,textAlign:'left' }}>
                A New Endorsement should be submitted when any one of the following changes to a policy (the original placement) has occurred:
              </Typography>
              <Box component="ul" sx={{ pl: 2, mb: 2 }}>
                <Typography component="li" variant="body1" sx={{ mb: 0.5 }}>
                  A change in Policy Premium (additional or returned)
                </Typography>
                <Typography component="li" variant="body1" sx={{ mb: 0.5 }}>
                  A change in <strong>Named Insured</strong>
                </Typography>
                <Typography component="li" variant="body1" sx={{ mb: 0.5 }}>
                  A change to the <strong>Location of Risk</strong> (for placements with multiple locations in Pennsylvania, the change endorsement should indicate if the location with the most exposure has been changed)
                </Typography>
                <Typography component="li" variant="body1" sx={{ mb: 0.5 }}>
                  A change or revision to the <strong>Policy Number</strong> (other than a policy rewrite)
                </Typography>
                <Typography component="li" variant="body1" sx={{ mb: 0.5 }}>
                  A change to the <strong>Policy Period</strong>
                </Typography>
              </Box>
              <Link href="https://www.pasla.org/Documents/ProcedureManual.pdf" target="_blank" rel="noopener noreferrer" sx={{ color: '#02A4E3' }}>
              https://www.pasla.org/Documents/ProcedureManual.pdf
              </Link>
            </Paper>

            {/* TEXAS Section */}
            <Typography variant="overline" sx={{...sectionTitleSx}}>TEXAS</Typography>
            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 3, backgroundColor: '#fff', borderColor: 'divider' }}>
              {/* <Typography variant="h6" sx={{ color: '#02A4E3', fontWeight: 'bold', mb: 2 }}>
                TEXAS
              </Typography> */}
              
              {/* Non-Premium Transactions Q&A Content */}
              <Box sx={{ mb: 2, textAlign: 'left' }}>
                <Typography variant="h6" sx={{ color: '#FF8C00', fontWeight: 'bold', mb: 2 }}>
                  Q. Are we required to file non-premium transactions?
                </Typography>
                
                <Typography variant="body1" sx={{ mb: 2,textAlign:'left' }}>
                  A. Yes; you must file the following non-premium transactions:
                </Typography>
                
                <Box component="ul" sx={{ pl: 2, mb: 2 }}>
                  <Typography component="li" variant="body1" sx={{ mb: 0.5 }}>
                    policy and or binder
                  </Typography>
                  <Typography component="li" variant="body1" sx={{ mb: 0.5 }}>
                    cancellations and reinstatements
                  </Typography>
                  <Typography component="li" variant="body1" sx={{ mb: 0.5 }}>
                    policy number change
                  </Typography>
                  <Typography component="li" variant="body1" sx={{ mb: 0.5 }}>
                    insured name change
                  </Typography>
                  <Typography component="li" variant="body1" sx={{ mb: 0.5 }}>
                    security or insurer change
                  </Typography>
                  <Typography component="li" variant="body1" sx={{ mb: 0.5 }}>
                    inception date change
                  </Typography>
                  <Typography component="li" variant="body1" sx={{ mb: 0.5 }}>
                    expiration date change
                  </Typography>
                </Box>
                
                <Typography variant="body1" sx={{ mb: 2,textAlign:'left' }}>
                  You may find instructions on how to do these transactions at{' '}
                  <Link href="https://www.sltx.org/education/training-videos/#" target="_blank" rel="noopener noreferrer" sx={{ color: '#02A4E3' }}>
                    https://www.sltx.org/education/training-videos/#
                  </Link>
                </Typography>
              </Box>
              
              <Link href="https://www.sltx.org/help/faq/online-filer-faqs/" target="_blank" rel="noopener noreferrer" sx={{ color: '#02A4E3' }}>
              https://www.sltx.org/help/faq/online-filer-faqs/
              </Link>
            </Paper>

            {/* WASHINGTON Section */}
            <Typography variant="overline" sx={{...sectionTitleSx}}>WASHINGTON</Typography>
            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 3, backgroundColor: '#fff', borderColor: 'divider' }}>
              {/* <Typography variant="h6" sx={{ color: '#02A4E3', fontWeight: 'bold', mb: 2 }}>
                WASHINGTON
              </Typography> */}
              <Typography variant="body1" sx={{ mb: 2,textAlign:'left' }}>
                (7) Endorsements or other modifications of coverages previously submitted must be promptly filed with the Association where the endorsement or other modification involves any additional or return premium, changes in security, or a change of policy number.
              </Typography>
              <Box component="ol" type='a' sx={{ pl: 2, mb: 2 }}>
                <Typography component="li" variant="body1" sx={{ mb: 0.5 }}>
                  Endorsements having additional or return premiums must include details, e.g. premiums, state tax, stamping fee.
                </Typography>
                <Typography component="li" variant="body1" sx={{ mb: 0.5 }}>
                  <strong>
                  Non-money endorsements need not be filed, except for changes of security or policy number.
                  </strong>
                </Typography>
                <Typography component="li" variant="body1" sx={{ mb: 0.5 }}>
                  Certifications should not be submitted with an endorsement.
                </Typography>
              </Box>
              <Link href="https://www.surpluslines.org/filing-information/" target="_blank" rel="noopener noreferrer" sx={{ color: '#02A4E3' }}>
              https://www.surpluslines.org/filing-information/
              </Link>
            </Paper>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            borderTop: 1,
            borderColor: 'divider',
            backgroundColor: 'grey.100',
            px: 3,
            py: 2,
            gap: 1.5,
          }}
        >
          <Button
            variant="outlined"
            startIcon={<CloseOutlinedIcon />}
            onClick={() => setShowReferenceSheetDialog(false)}
            sx={{ fontWeight: 600, textTransform: 'none' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NonPremiumEndorsements;
